import React from 'react';

import {assert} from 'Asserts';
import BioentitySearchInput from 'inventory/BioentitySearchInput';
import BatchSearchInput from 'samples/batches/BatchSearchInput';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import BulkAddWorkflowInputsModal from 'workflows/modals/BulkAddWorkflowInputsModal';
import {InputType} from 'workflows/stage-entries/components/ExternalInputPicker';

interface SearchInputItem {
  id: string;
  name: string;
}

// At most one of batch or file should be specified
export interface StageRunRow {
  batch?: SearchInputItem | null;
  file?: SearchInputItem | null;
  inputType: InputType;
}

interface WorkflowInputTableProps {
  initialStageRunRows: Array<StageRunRow>;
  initialStageRunRowsChanged: (stageRunRows: Array<StageRunRow | null>) => void;
}

export default class WorkflowInputTable extends React.Component<WorkflowInputTableProps> {
  _handleRowAdded(inputType: InputType): void {
    const newRow = {inputType};
    this.props.initialStageRunRowsChanged([...this.props.initialStageRunRows, newRow]);
  }

  _handleBatchChanged(index: number, batch: SearchInputItem | null): void {
    assert(this.props.initialStageRunRows[index].inputType === InputType.BATCH, 'Row is for selecting batch');
    const newRows = this.props.initialStageRunRows.map((row, i) => (i === index ? {...row, batch} : row));
    this.props.initialStageRunRowsChanged(newRows);
  }

  _handleFileChanged(index: number, file: SearchInputItem | null): void {
    assert(this.props.initialStageRunRows[index].inputType === InputType.FILE, 'Row is for selecting entity');
    const newRows = this.props.initialStageRunRows.map((row, i) => (i === index ? {...row, file} : row));
    this.props.initialStageRunRowsChanged(newRows);
  }

  _handleRowDeleted(index: number): void {
    const newRows = this.props.initialStageRunRows.filter((row, i) => i !== index);
    this.props.initialStageRunRowsChanged(newRows);
  }

  _handleBulkAddWorkflowInputs = async (): Promise<void> => {
    const importedSamples = await legacyRunModal(BulkAddWorkflowInputsModal, {});
    if (importedSamples) {
      const newRows = importedSamples.map((sample) =>
        sample.batchSummary
          ? {inputType: InputType.BATCH, batch: sample.batchSummary}
          : {inputType: InputType.FILE, file: sample.entitySummary}
      );
      const nonEmptyRows = this.props.initialStageRunRows.filter(
        ({batch, file}) => batch != null || file != null
      );
      this.props.initialStageRunRowsChanged([...nonEmptyRows, ...newRows]);
    }
  };

  _renderHeader(): JSX.Element {
    return (
      <div className="simpleDivTable-headerCell">
        <div className="u-small-spacing-bottom u-vertical-center-items">
          <span>Input Samples</span>
          <Dropdown
            menu={
              <Menu className="workflowTable-addDropdown" height={MenuHeight.MEDIUM} width={MenuWidth.LARGE}>
                <Option
                  icon="compose"
                  onSelect={() => {
                    this._handleRowAdded(InputType.FILE);
                  }}
                >
                  Add entity
                </Option>
                <Option
                  icon="upload"
                  onSelect={() => {
                    this._handleBulkAddWorkflowInputs();
                  }}
                >
                  Import from spreadsheet
                </Option>
              </Menu>
            }
          >
            <BenchlingIcon className="workflowInputTable-addButton u-small-spacing-left" icon="compose" />
          </Dropdown>
        </div>
      </div>
    );
  }

  _renderDeleteButtonHeader(): JSX.Element {
    return <div className="workflowInputTable-deleteIcon-placeholder" />;
  }

  _renderRow(index: number, initializationInfo: StageRunRow): JSX.Element {
    return (
      <div className="simpleDivTable-row workflowConfigureScreen-deletable-row" key={index}>
        <div className="simpleDivTable-cell">
          {initializationInfo.inputType === InputType.BATCH ? (
            <BatchSearchInput
              autoFocus={false}
              onChange={(batches: Array<SearchInputItem>) => {
                this._handleBatchChanged(index, batches.length ? batches[0] : null);
              }}
              selectedBatches={initializationInfo.batch ? [initializationInfo.batch] : []}
            />
          ) : (
            <BioentitySearchInput
              onChange={(files: Array<SearchInputItem>) => {
                this._handleFileChanged(index, files.length ? files[0] : null);
              }}
              selectedItems={initializationInfo.file ? [initializationInfo.file] : []}
            />
          )}
        </div>
        <div className="workflowInputTable-deleteIcon-placeholder u-flex-display">
          <BenchlingIcon
            className="workflowConfigureScreen-delete faded u-danger-pointer"
            icon="delete"
            onClick={() => {
              this._handleRowDeleted(index);
            }}
          />
        </div>
      </div>
    );
  }

  override render(): JSX.Element {
    return (
      <div className="metadata-table simpleDivTable workflowInputTable">
        <div className="simpleDivTable-header">
          {this._renderHeader()}
          {this._renderDeleteButtonHeader()}
        </div>
        {(() => this.props.initialStageRunRows.map((row, i) => this._renderRow(i, row)))()}
      </div>
    );
  }
}
